import { Alert, AppShellContent, Stack, Text, Title } from "@assetwatch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cmmsVendorsQueryOptions } from "../queries/useCmmsVendors";
import { CmmsIntegrationScreen } from "../screens/CmmsIntegrationScreen";

/**
 * Deep-link target for a single Marketplace capability (IWA-19387, Scenario 4).
 *
 * `capabilityId` is a stable slug so another AssetWatch app can link straight
 * to a capability without routing through the Marketplace home page, e.g.
 *   /capabilities/cmms-integration
 *
 * Add new capabilities to CAPABILITIES; no routing changes needed.
 */

const CAPABILITIES = {
  "cmms-integration": {
    titleKey: "marketplace.cmms.title",
    descriptionKey: "marketplace.cmms.capabilityDescription",
    screen: CmmsIntegrationScreen,
  },
} as const;

type CapabilityId = keyof typeof CAPABILITIES;

function isKnownCapability(value: string): value is CapabilityId {
  return Object.hasOwn(CAPABILITIES, value);
}

export const Route = createFileRoute("/_app/capabilities/$capabilityId")({
  // Only CMMS needs vendor data today. Prefetching in the loader keeps the
  // header and content appearing together rather than flashing a spinner.
  // prefetchQuery never rejects, so a failed load stays in the query and the
  // panel shows its own error instead of the route falling to the root error.
  loader: ({ context, params }) =>
    params.capabilityId === "cmms-integration"
      ? context.queryClient.prefetchQuery(cmmsVendorsQueryOptions())
      : undefined,
  component: CapabilityRoute,
  staticData: { pageHeader: CapabilityPageHeader },
});

function CapabilityPageHeader() {
  const { t } = useTranslation();
  const { capabilityId } = Route.useParams();
  const capability = isKnownCapability(capabilityId)
    ? CAPABILITIES[capabilityId]
    : undefined;

  return (
    <Stack gap={8}>
      <Title size="lg">
        {capability
          ? t(capability.titleKey)
          : t("marketplace.capabilityFallbackTitle")}
      </Title>
      {capability ? (
        <Text tone="subtle">{t(capability.descriptionKey)}</Text>
      ) : null}
    </Stack>
  );
}

function CapabilityRoute() {
  const { t } = useTranslation();
  const { capabilityId } = Route.useParams();

  if (!isKnownCapability(capabilityId)) {
    return (
      <AppShellContent>
        <Alert
          subtext={t("marketplace.capabilityNotFoundDescription", {
            capabilityId,
          })}
          title={t("marketplace.capabilityNotFoundTitle")}
          tone="warning"
        />
      </AppShellContent>
    );
  }

  const Screen = CAPABILITIES[capabilityId].screen;

  return (
    <AppShellContent>
      <Screen />
    </AppShellContent>
  );
}
